/**
 * QuickStart information for Models product.
 */

import {MenuItem} from '@material-ui/core';
import {TargetBlank} from '@wandb/weave/common/util/links';
import {Icon} from '@wandb/weave/components/Icon';
import copyToClipboard from 'copy-to-clipboard';
import React, {ChangeEvent, useCallback, useEffect, useState} from 'react';
import {Slide, toast} from 'react-toastify';

import config from '../../config';
import {useViewerApiKeyQuery} from '../../generated/graphql';
import * as Track from '../../services/analytics';
import {Analytics, QuickStartAnalyticsOrigin} from '../../services/analytics';
import {CodeSnippet} from '../CodeSnippet';
import {FRAMEWORKS} from './frameworks';
import * as S from './QuickStart.styles';
import {Product} from './QuickStartProductSelect';
import {Framework, FrameworkCategory, FrameworkName} from './types';
import {mapByKey} from './utils';

type QuickStartModelsProps = {
  entityName: string;
  projectName: string;
  analyticsOrigin: QuickStartAnalyticsOrigin;
  maxWidth: number;
  onVariantActivated: (product: Product, variant: string) => void;
};

export const QuickStartModels = ({
  entityName,
  projectName,
  analyticsOrigin,
  maxWidth,
  onVariantActivated,
}: QuickStartModelsProps) => {
  const frameworks = FRAMEWORKS.filter(
    framework => framework.frameworkCategory === FrameworkCategory.MODELS
  );
  const [selectedFramework, setSelectedFramework] = useState<Framework>(
    frameworks[0]
  );

  const viewerApiKey = useViewerApiKeyQuery();
  const apiKey = viewerApiKey.data?.viewer?.apiKey ?? '';

  const frameworkNameMap = mapByKey(FRAMEWORKS, 'value');

  useEffect(() => {
    // Change this if we ever add another Models quickstart variant.
    // For now, we only have a code variant.
    onVariantActivated(Product.Models, 'models-code');
  }, [onVariantActivated]);

  const onFrameworkSelectChange = (e: ChangeEvent<{value: unknown}>) => {
    const newFrameworkName = e.target.value as FrameworkName;
    const framework: Framework =
      frameworkNameMap.get(newFrameworkName) ?? FRAMEWORKS[0];
    setSelectedFramework(framework);
    Analytics.track('Quick Start: Framework Selected', {
      frameworkName: newFrameworkName,
    });
  };

  const onApiKeyCopyClick = useCallback(() => {
    copyToClipboard(apiKey ?? '');
    toast('Copied to clipboard', {
      transition: Slide,
    });

    Track.copiedApiKey({
      projectName,
      entityName,
      location: analyticsOrigin,
      frameworkName: selectedFramework.value,
    });
  }, [
    analyticsOrigin,
    apiKey,
    entityName,
    projectName,
    selectedFramework.value,
  ]);

  const isOnPrem = config.ENVIRONMENT_IS_PRIVATE;
  const loginCode = isOnPrem
    ? `wandb login --host=${window.location.origin}`
    : 'wandb login';

  return (
    <>
      <S.QuickStartHeader>
        <S.QuickStartHeaderText>
          Quickstart: Tracking your first run in Weights & Biases
        </S.QuickStartHeaderText>
        <p>
          Weights & Biases' tools make it easy for you to quickly track
          experiments, visualize results, spot regressions, and more. Simply
          put, Weights & Biases enables you to build better models faster and
          easily share findings with colleagues.
        </p>
      </S.QuickStartHeader>
      <S.QuickStartFrameworkSelectionRow $maxWidth={maxWidth}>
        <S.QuickStartFrameworkSelectionLabel>
          Visualize your model training with
        </S.QuickStartFrameworkSelectionLabel>
        <S.QuickStartFrameworkOptions $maxWidth={maxWidth}>
          <S.QuickStartFrameworkSelect
            labelId="quickstart-framework-select-label"
            id="quickstart-framework-select"
            variant="outlined"
            value={selectedFramework.value}
            onChange={onFrameworkSelectChange}>
            {frameworks.map(framework => (
              <MenuItem key={framework.value} value={framework.value}>
                <S.FrameworkItem>{framework.iconWithName}</S.FrameworkItem>
              </MenuItem>
            ))}
          </S.QuickStartFrameworkSelect>
          <S.ResponsiveContainer>
            <S.FrameworkSelectionConjunction>
              or
            </S.FrameworkSelectionConjunction>
            <TargetBlank href={selectedFramework.colabLink}>
              <S.ColabButton
                variant="contained"
                color="default"
                onClick={() => {
                  Track.clickedOpenInColab({
                    projectName,
                    entityName,
                    location: analyticsOrigin,
                    frameworkName: selectedFramework.value,
                    colabLink: selectedFramework.colabLink,
                  });
                }}
                startIcon={
                  <S.QuickStartIcon>
                    <Icon name="logo-colab" />
                  </S.QuickStartIcon>
                }>
                Open in Colab
              </S.ColabButton>
            </TargetBlank>
          </S.ResponsiveContainer>
        </S.QuickStartFrameworkOptions>
      </S.QuickStartFrameworkSelectionRow>
      <S.QuickStartOrderedList>
        <S.QuickStartListItem>Set up the wandb library</S.QuickStartListItem>
        <S.QuickStartStepContent>
          <p>
            Install the CLI and Python library for interacting with the Weights
            and Biases API.
          </p>
          <CodeSnippet
            codeSnippet="pip install wandb"
            analyticsEvent={Track.copiedCodeBlock}
            analyticsDefaultProperties={{
              projectName,
              entityName,
              location: analyticsOrigin,
              frameworkName: selectedFramework.value,
            }}
          />
          <p>Next, log in and paste your API key when prompted.</p>
          <CodeSnippet
            codeSnippet={loginCode}
            analyticsEvent={Track.copiedCodeBlock}
            analyticsDefaultProperties={{
              projectName,
              entityName,
              location: analyticsOrigin,
              frameworkName: selectedFramework.value,
            }}
          />
        </S.QuickStartStepContent>
        {apiKey && (
          <S.UserApiKeyRow analyticsOrigin={analyticsOrigin}>
            <S.UserApiKeyLabel>
              <S.QuickStartIcon>
                <Icon name="key-admin" />
              </S.QuickStartIcon>
              <p>
                <b>Your API key</b> for logging in to the wandb library.
              </p>
            </S.UserApiKeyLabel>
            <S.UserApiKeyBox onClick={onApiKeyCopyClick}>
              <p>{apiKey}</p>
              <S.QuickStartIcon>
                <Icon name="copy" />
              </S.QuickStartIcon>
            </S.UserApiKeyBox>
          </S.UserApiKeyRow>
        )}
        <S.QuickStartListItem>Log a Run to a new project</S.QuickStartListItem>
        <S.QuickStartStepContent>
          <p>
            Start tracking system metrics and console logs, right out of the
            box. Run this sample code to see the new run appear in W&B.
          </p>
          <CodeSnippet
            showExpandCollapse
            codeSnippet={selectedFramework.codeSnippet}
            analyticsEvent={Track.copiedCodeBlock}
            analyticsDefaultProperties={{
              projectName,
              entityName,
              location: analyticsOrigin,
              frameworkName: selectedFramework.value,
            }}
          />
        </S.QuickStartStepContent>
        <S.QuickStartListItem>
          See live metrics, terminal logs, and system stats in your project
        </S.QuickStartListItem>
        <S.QuickStartStepContent>
          <p>
            <span role="img" aria-label="tada-emoji">
              🎉
            </span>
            Congrats! Now that you've run the code, navigate to your newly
            created project to compare the runs and their metrics.
          </p>
        </S.QuickStartStepContent>
      </S.QuickStartOrderedList>
    </>
  );
};
